import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Lock, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordStrength from "@/components/PasswordStrength";
import { toast } from "sonner";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw !== pw2) return setErr("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => nav("/login"), 1200);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-secondary" /></div>
          <span className="font-display font-semibold text-lg">Treasurescare</span>
        </Link>
        {done ? (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 text-primary flex items-center justify-center"><CheckCircle2 className="w-8 h-8" /></div>
            <h1 className="mt-6 font-display text-3xl">Password updated.</h1>
            <p className="mt-2 text-muted-foreground text-sm">Redirecting to sign in…</p>
          </div>
        ) : (
          <>
            <div className="overline text-secondary">Set a new password</div>
            <h1 className="mt-2 font-display text-4xl font-light tracking-tight">Choose a new password.</h1>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label className="text-xs">New password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input required type="password" data-testid="reset-pw-input" value={pw} onChange={e => setPw(e.target.value)} className="pl-9" placeholder="At least 8 characters" />
                </div>
                <PasswordStrength password={pw} className="mt-2" />
              </div>
              <div>
                <Label className="text-xs">Confirm new password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input required type="password" data-testid="reset-pw2-input" value={pw2} onChange={e => setPw2(e.target.value)} className="pl-9" />
                </div>
              </div>
              {err && <div className="text-sm text-destructive">{err}</div>}
              <Button type="submit" data-testid="reset-submit-btn" disabled={loading || !token} className="w-full">
                {loading ? "Updating…" : "Update password"}
              </Button>
              {!token && <div className="text-xs text-destructive">Missing reset token. Please use the link from your email.</div>}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
